/**
 * The staff line of the knowledge switch (Arjun, 2026-10-23: a plant admin opened the Knowledge page the moment the
 * switch went on and saw only zeros, though the store held findings and questions). What the org's own people see of
 * its knowledge store, by the customer view's own rule, and what the Sahas team still holds back, in plain sentences, for
 * the Knowledge page's internal view and the console's toast. Pure.
 */
import type { KnowledgeCustomerView } from "@/lib/api/platform-admin";

type Sees = KnowledgeCustomerView["customer_sees"];

function count(n: number, one: string, many: string): string {
  return `${n.toLocaleString("en-GB")} ${n === 1 ? one : many}`;
}

function joinAnd(parts: string[]): string {
  if (parts.length <= 1) return parts.join("");
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/** Whether the plant sees nothing at all: no finding and no question. */
export function plantSeesNothing(s: Sees): boolean {
  return s.findings === 0 && s.questions === 0;
}

/** What the plant sees: "The plant sees 2 findings and 1 question." With the switch off, what it would see. */
export function plantSeesLine(s: Sees, visible: boolean): string {
  const what = `${count(s.findings, "finding", "findings")} and ${count(s.questions, "question", "questions")}`;
  return visible
    ? `The plant sees ${what}.`
    : `Knowledge is off for the plant. Turned on, it would see ${what}.`;
}

/**
 * What the Sahas team still holds back, and why, a sentence for each kind that counts any: "12 candidate findings,
 * counting any data checks, and 3 draft questions wait for your review. 2 confirmed checks of the data are never shown
 * to the plant. 1 confirmed finding is marked internal, so only the Sahas team sees it." "" when nothing is held back.
 * The candidates count the data checks too, which the page lists apart from its Candidates tab, so the line says so (the
 * 2026-10-24 sweep, item 16.2: a count of candidates above the tab's own).
 */
export function plantHeldBackLine(s: Sees): string {
  const h = s.held_back;
  const review: string[] = [];
  if (h.candidates > 0) review.push(`${count(h.candidates, "candidate finding", "candidate findings")}, counting any data checks,`);
  if (h.draft_questions > 0) review.push(count(h.draft_questions, "draft question", "draft questions"));
  const out: string[] = [];
  if (review.length > 0) {
    const one = review.length === 1 && (h.candidates === 1 || h.draft_questions === 1);
    out.push(`${joinAnd(review)} ${one ? "waits" : "wait"} for your review.`);
  }
  if (h.data_quality > 0) {
    out.push(`${count(h.data_quality, "confirmed check of the data is", "confirmed checks of the data are")} never shown to the plant.`);
  }
  if (h.internal > 0) {
    out.push(`${count(h.internal, "confirmed finding is", "confirmed findings are")} marked internal, so only the Sahas team sees ${h.internal === 1 ? "it" : "them"}.`);
  }
  return out.join(" ");
}

/** The console's toast once the switch is on: what the plant sees now, and a warning when that is nothing. */
export function knowledgeShownToast(name: string, s: Sees): { tone: "ok" | "warn"; text: string } {
  const held = plantHeldBackLine(s);
  if (plantSeesNothing(s)) {
    return {
      tone: "warn",
      text: `Knowledge shown to ${name}, but the plant sees nothing yet: no finding is confirmed and marked for it, and no question is approved.${held ? ` ${held}` : ""}`,
    };
  }
  return { tone: "ok", text: `Knowledge shown to ${name}. ${plantSeesLine(s, true)}${held ? ` ${held}` : ""}` };
}
